import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ContactSupportOutlinedIcon from '@mui/icons-material/ContactSupportOutlined';
import { ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import GoalIcon from '../../../Assets/Icons/goals.svg';
import HomeIcon from '../../../Assets/Icons/home.svg';
import OffersIcon from '../../../Assets/Icons/offers.svg';
import StartupIcon from '../../../Assets/Icons/startup1.svg';
import MentorIcon from '../../../Assets/Icons/mentor.svg';
import routes from '../../../Utils/routes';
import { useStyles } from '../styles';
import ArticleIcon from '@mui/icons-material/Article';
import juryportal from '../../../Assets/Icons/juryportal.svg';
import storageService from '../../../Utils/storageService';

interface SideNavProps {
  handleListClick: (link: string, index: number) => void;
  hideSideBar: boolean;
}

const SideNavTabs: React.FunctionComponent<SideNavProps> = ({ handleListClick, hideSideBar }) => {
  const classes = useStyles();
  const { pathname } = useLocation();
  const [openMenus, setOpenMenus] = useState<{ [key: string]: boolean }>({});
  const [storedData, setStoredData] = useState<string>('');

  useEffect(() => {
    const loadUserPreferences = async () => {
      try {
        // Load preferences from storageService
        const preferencesData = await storageService.getItem('preferences');
        if (preferencesData) {
          const activeRoles = Array.isArray(preferencesData) ? preferencesData : JSON.parse(preferencesData);
          // Store all roles as JSON string to handle multiple selections
          setStoredData(JSON.stringify(activeRoles));
        } else {
          // Fallback to old userRole method
          const userRole = await storageService.getItem('userRole') || '';
          setStoredData(JSON.stringify([userRole]));
        }
      } catch (error) {
        console.error('Error loading user preferences:', error);
      }
    };

    loadUserPreferences();
  }, []);

  console.log(storedData, 'storedData');

  const rolePermissions: Record<string, string[]> = {
    Startup: ['Goals and Milestones', 'Event', 'Service & Support', 'Mentors', 'Startups', 'Venue Booking', 'Offers'],
    Mentor: ['Goals and Milestones', 'Event', 'Service & Support', 'Startups', 'Venue Booking',  'Offers'],
    Member: ['Event', 'Service & Support', 'Venue Booking'],
    Guest: ['Venue Booking'], // Guest users only see Venue Booking
    guest: ['Venue Booking'], // Handle lowercase guest as well
    Partner: ['Venue Booking'],
    Jury: ['Event', 'Service & Support', 'Venue Booking', 'Jury Portal'],
    JuryPortal: ['Jury Portal'],
    VenuePortal: ['Venue Booking'],
    // Add accountRole permissions (same as Startup for team members)
    Startup_Team_Member: ['Goals and Milestones', 'Event', 'Service & Support', 'Mentors', 'Startups', 'Venue Booking', 'Offers'],
    // Special handling for when all roles are selected
    All: ['Goals and Milestones', 'Event', 'Service & Support', 'Mentors', 'Startups', 'Venue Booking', 'Offers', 'Jury Portal'],
  };

  // Function to get combined permissions from multiple roles
  const getCombinedPermissions = (roles: string[]): string[] => {
    const allPermissions = new Set<string>();
    
    roles.forEach(role => {
      const permissions = rolePermissions[role] || [];
      permissions.forEach(permission => allPermissions.add(permission));
    });
    
    return Array.from(allPermissions);
  };

  // Default Home menu (available for all users)
  const sideNavContents = [
    {
      icon: (isActive: boolean) => <img src={HomeIcon} className={isActive ? classes.listItemIconActive : classes.listItemIcon} />,
      label: 'Home',
      link: routes.home,
      name: 'Home',
    },
    {
      icon: (isActive: boolean) => <img src={GoalIcon} className={isActive ? classes.listItemIconActive : classes.listItemIcon} />,
      label: 'Goals & Milestones',
      link: routes.goalsAndMilestones,
      name: 'Goals and Milestones',
    },
    {
      icon: (isActive: boolean) => <AutoAwesomeIcon className={isActive ? classes.listItemIconActive : classes.listItemIcon} />,
      label: 'Events',
      link: routes.events,
      name: 'Event',
    },
    {
      icon: (isActive: boolean) => <ContactSupportOutlinedIcon className={isActive ? classes.listItemIconActive : classes.listItemIcon} />,
      label: 'Services & Support',
      link: routes.support,
      name: 'Service & Support',
    },
    {
      icon: (isActive: boolean) => <img src={StartupIcon} alt="Startup" className={isActive ? classes.listItemIconActive : classes.listItemIcon} />,
      label: 'Startups',
      link: routes.startups,
      name: 'Startups',
    },
    {
      icon: (isActive: boolean) => <img src={MentorIcon} className={isActive ? classes.listItemIconActive : classes.listItemIcon} />,
      label: 'Mentors',
      link: routes.mentors,
      name: 'Mentors',
    },
    {
      icon: (isActive: boolean) => <ArticleIcon className={isActive ? classes.listItemIconActive : classes.listItemIcon} />,
      label: 'Venue Booking',
      link: routes.facilities,
      name: 'Venue Booking',
    },
    {
      icon: (isActive: boolean) => <img src={juryportal} style={{ width: 17, marginLeft: 2 }} className={isActive ? classes.listItemIconActive : classes.listItemIcon} />,
      label: 'Jury Portal',
      link: routes.juryDashboard,
      name: 'Jury Portal',
    },
    {
      icon: (isActive: boolean) => <img src={HomeIcon} className={isActive ? classes.listItemIconActive : classes.listItemIcon} />,
      label: 'Community',
      link: routes.startups,
      name: 'Community',
    },
    {
      icon: (isActive: boolean) => <img src={OffersIcon} alt='Offers' className={isActive ? classes.listItemIconActive : classes.listItemIcon} />,
      label: 'Offers',
      link: routes.deals,
      name: 'Offers',
    },
  ];
  // Get active roles from preferences
  const getActiveRoles = (): string[] => {
    try {
      if (storedData) {
        const parsedRoles = JSON.parse(storedData);
        return Array.isArray(parsedRoles) ? parsedRoles : [parsedRoles];
      }
      return [];
    } catch (error) {
      console.error('Error getting active roles:', error);
      // If parsing fails, treat as single role
      return storedData ? [storedData] : [];
    }
  };

  const activeRoles = getActiveRoles();
  
  // Check if user selected all preferences (multiple roles)
  const hasMultipleRoles = activeRoles.length > 1;
  
  // Get combined permissions based on all active roles
  const combinedPermissions = getCombinedPermissions(activeRoles);

  console.log('Active roles:', activeRoles);
  console.log('Has multiple roles:', hasMultipleRoles);
  console.log('Combined permissions:', combinedPermissions);

  const filteredSideNav = sideNavContents.filter((item) => {
    // For multiple roles or when showing all, be more inclusive with Home visibility
    if (hasMultipleRoles) {
      // Show all items that have permissions in the combined set
      return combinedPermissions.includes(item.name) || item.name === 'Home';
    }
    
    // Original logic for single role selection
    // Hide Home for Member, Jury, and Guest users
    if ((activeRoles.includes('Member') || activeRoles.includes('Jury') || activeRoles.includes('Guest') || activeRoles.includes('guest')) && item.name === 'Home') {
      return false;
    }
    return combinedPermissions.includes(item.name) || item.name === 'Home';
  });

  // Add role-specific items to the top based on active roles
  const prioritizedItems: any[] = [];

  // Always add Home first if it's available in the filtered navigation
  const homeItem = sideNavContents.find((item) => item.name === 'Home');
  if (homeItem && filteredSideNav.includes(homeItem)) {
    prioritizedItems.push(homeItem);
  }

  // For multiple roles, prioritize based on the most common access patterns
  if (hasMultipleRoles) {
    // Add high-priority items for multi-role users (after Home)
    if (combinedPermissions.includes('Goals and Milestones')) {
      prioritizedItems.push(sideNavContents.find((item) => item.name === 'Goals and Milestones'));
    }
    if (combinedPermissions.includes('Event')) {
      prioritizedItems.push(sideNavContents.find((item) => item.name === 'Event'));
    }
    if (combinedPermissions.includes('Jury Portal')) {
      prioritizedItems.push(sideNavContents.find((item) => item.name === 'Jury Portal'));
    }
    if (combinedPermissions.includes('Venue Booking')) {
      prioritizedItems.push(sideNavContents.find((item) => item.name === 'Venue Booking'));
    }
  } else {
    // Original single-role prioritization logic (after Home)
    if (activeRoles.includes('Guest') || activeRoles.includes('guest')) {
      // For guest users, add Venue Booking after Home
      prioritizedItems.push(
        sideNavContents.find((item) => item.name === 'Venue Booking')
      );
    } else if (activeRoles.includes('Member')) {
      prioritizedItems.push(
        sideNavContents.find((item) => item.name === 'Venue Booking')
      );
    } else if (activeRoles.includes('Panelist')) {
      prioritizedItems.push(
        sideNavContents.find((item) => item.name === 'Jury Portal'),
        sideNavContents.find((item) => item.name === 'Venue Booking')
      );
    } else if (activeRoles.includes('VenuePortal')) {
      prioritizedItems.push(
        sideNavContents.find((item) => item.name === 'Venue Booking')
      );
    } else if (activeRoles.includes('Startup_Team_Member') || activeRoles.includes('Startup_Founder')) {
      // For startup account roles, prioritize startup-related features
      prioritizedItems.push(
        sideNavContents.find((item) => item.name === 'Goals and Milestones'),
        sideNavContents.find((item) => item.name === 'Event'),
        sideNavContents.find((item) => item.name === 'Mentors')
      );
    } else if (activeRoles.includes('Mentor_Individual')) {
      // For mentor account roles, prioritize mentor-related features
      prioritizedItems.push(
        sideNavContents.find((item) => item.name === 'Goals and Milestones'),
        sideNavContents.find((item) => item.name === 'Startups'),
        sideNavContents.find((item) => item.name === 'Event')
      );
    }
  }

  // Remove null values from prioritizedItems
  const topItems = prioritizedItems.filter(Boolean);

  // Final ordered list
  const finalSideNav = [...topItems, ...filteredSideNav.filter((item) => !topItems.includes(item))];

  return (
    <>
      {finalSideNav.map((item: any, index) => (
        <ListItemButton
          key={index}
          onClick={() => handleListClick(item.link || '', index)}
          sx={{ marginTop: index === 0 ? { xs: '10px', sm: '15px', md: '25px', lg: '35px' } : '12px' }}
          className={`${classes.listItemButton} ${pathname.startsWith(item.link) && classes.listItemButtonActive}`}
        >
          <ListItemIcon sx={{ minWidth: '40px' }}>{item.icon(pathname.startsWith(item.link))}</ListItemIcon>
          {!hideSideBar && (
            <ListItemText className={`${classes.listItemText} ${pathname.startsWith(item.link) && classes.listItemTextActive}`} sx={{ display: { xs: 'none', sm: 'none', md: 'none', lg: 'block' } }}>
              {item.label}
            </ListItemText>
          )}
        </ListItemButton>
      ))}
    </>
  );
};
export default SideNavTabs;
